import { SortDirection } from './table.definitions';

export type SortableKey<T> = { [K in keyof T]: T[K] extends Sortable ? K : never }[keyof T];

type Sortable = number | string | boolean | null | undefined;
type Selector<T> = (x: T) => Sortable;
type SortValue<T> = { Value: T, First: Sortable, Second?: Sortable };

// Unlike sort, orderBy and orderByKey are not in-place (they create a new array)
declare global {
    interface Array<T> {
        distinct(selector?: (x: T) => any): T[];
        distinctByKey(key: keyof T, caseSensitive?: boolean): T[];
        orderBy(firstSelector: Selector<T>, firstDirection?: SortDirection, secondSelector?: Selector<T>, secondDirection?: SortDirection): T[];
        orderByKey(firstKey: SortableKey<T>, firstDirection?: SortDirection, firstCaseSensitive?: boolean,
                   secondKey?: SortableKey<T>, secondDirection?: SortDirection, secondCaseSensitive?: boolean): T[];
        sum(this: number[]): number;
        sumBy(selector: (x: T) => number): number;
    }
}

Array.prototype.distinctByKey = function<T>(this: T[], key: SortableKey<T>, caseSensitive?: boolean) {
    return DistinctByKey(this, key, caseSensitive);
}

Array.prototype.distinct = function<T>(this: T[], selector?: (x: T) => any) {
    return Distinct(this, selector);
}

Array.prototype.orderByKey = function<T>(this: T[], firstKey: SortableKey<T>, firstDirection?: SortDirection, firstCaseSensitive?: boolean,
                                         secondKey?: SortableKey<T>, secondDirection?: SortDirection, secondCaseSensitive?: boolean) {
    return OrderByKey(this, firstKey, firstDirection, firstCaseSensitive, secondKey, secondDirection, secondCaseSensitive);
}

Array.prototype.orderBy = function<T>(this: T[], firstSelector: Selector<T>, firstOrder?: SortDirection, secondSelector?: Selector<T>, secondOrder?: SortDirection) {
    return OrderBySelector(this, firstSelector, firstOrder, secondSelector, secondOrder);
}

Array.prototype.sumBy = function<T>(this: T[], selector: (x: T) => any) {
    return SumBy(this, selector);
}

Array.prototype.sum = function(this: number[]) {
    return Sum(this);
}

export function SumBy<T>(array: T[] | null | undefined, selector: (x: T) => any) {
    return array?.reduce((total, element) => total + selector(element), 0) ?? 0;
}

export function Sum(array: number[] | null | undefined) {
    return array?.reduce((total, value) => total + value, 0) ?? 0;
}

export function DistinctByKey<T>(array: T[] | null | undefined, key: keyof T, caseSensitive?: boolean) {
    if (!array?.length) {
        return [];
    }

    // NOT case-insensitive by default for string arrays (distinct is less likely to have this as a desirable feature, e.g., IDs)
    const selector = caseSensitive === false ? (obj: T) => String(obj[key]).toLocaleLowerCase() : (obj: T) => obj[key];

    return Distinct(array, selector);
}

export function Distinct<T>(array: T[] | null | undefined, selector?: (x: T) => any): T[] {
    if (!array?.length) {
        return [];
    }

    if (!selector) {
        selector = x => x;
    }

    const keys = new Set<T>();
    return array.filter(value => {
        const key = selector(value);
        return !keys.has(key) && keys.add(key);
    });
}

export function OrderByKey<T>(array: T[] | null | undefined, firstKey: SortableKey<T>, firstDirection?: SortDirection, firstCaseSensitive?: boolean,
                              secondKey?: SortableKey<T>, secondDirection?: SortDirection, secondCaseSensitive?: boolean) {
    if (!array?.length) {
        return [];
    }

    // Case-insensitive by default for string arrays
    const firstSelector = KeySelector(firstKey, firstCaseSensitive ?? typeof(array[0][firstKey]) !== 'string');
    const secondSelector = secondKey ? KeySelector(secondKey, secondCaseSensitive ?? typeof(array[0][secondKey]) !== 'string') : undefined;

    return OrderBySelector(array, firstSelector, firstDirection, secondSelector, secondDirection);
}

export function OrderBySelector<T>(array: T[] | null | undefined, firstSelector: Selector<T>, firstOrder?: SortDirection, secondSelector?: Selector<T>, secondOrder?: SortDirection): T[] {
    if (!array?.length) {
        return [];
    }

    const secondSelectorWithDefault = secondSelector ?? ((_: T) => undefined);
    const sortArray: SortValue<T>[] = array.map(value => ({
        Value: value,
        First: firstSelector(value),
        Second: secondSelectorWithDefault(value)
    }));

    sortArray.sort(Comparator(firstOrder, secondOrder));

    return sortArray.map(item => item.Value);
}

function KeySelector<T>(key: SortableKey<T>, caseSensitive: boolean = false) {
    return caseSensitive ? (obj: T) => obj[key] as Sortable
                         : (obj: T) => String(obj[key]).toLocaleLowerCase();
}

function Comparator<T>(firstOrder?: SortDirection, secondOrder?: SortDirection) {
    const firstSign = firstOrder === SortDirection.Descending ? -1 : 1;
    const secondSign = secondOrder === SortDirection.Descending ? -1 : 1;

    return (a: SortValue<T>, b: SortValue<T>) =>
          a.First! < b.First! ? -firstSign
        : a.First! > b.First! ? firstSign
        : a.Second === b.Second ? 0
        : a.Second! < b.Second! ? -secondSign
        : secondSign
}
